import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import S from './StatusSensor.module.scss';
import { MdAccessTime } from 'react-icons/md';

interface StatusSensorProps {
  sensorId?: string | number;
  status?: number; // 0-10: 0=off, 1=on, 2-10=autres modes
  color?: string; // Couleur personnalisée du mode (basevie)
  modeName?: string; // Nom du mode
  datetime?: string; // Date au format "YYYY-MM-DD HH:MM:SS"
  lastCommandStatus?: string;
}

const StatusSensor: React.FC<StatusSensorProps> = ({
  sensorId,
  status,
  color,
  modeName,
  datetime,
  lastCommandStatus,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const hasData = !!(modeName || datetime || status !== undefined || color);
  const lastStatusDate = datetime
    ? new Date(datetime.replace(' ', 'T'))
    : undefined;
  const isPending = lastCommandStatus === 'PENDING';

  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.top - 12, // Espace entre le ticket et la popup
        left: rect.left + rect.width / 2, // centré
      });
      setShowPopup(true);
    }
  };

  const getStatusLabel = (): string => {
    if (modeName) return modeName;
    if (status !== undefined) return `Mode ${status}`;
    return 'Pas de réponse du capteur';
  };

  const getStatusClass = (): string => {
    if (color) return '';
    if (!hasData) return S.statusOff;
    return S.statusMode;
  };

  const getCustomStyle = () => {
    if (!color) return {};
    return {
      background: `linear-gradient(135deg, ${color} 0%, ${adjustBrightness(
        color,
        -15
      )} 100%)`,
      border: `1px solid ${adjustBrightness(color, -20)}`,
      color: 'white',
    };
  };

  // Fonction pour assombrir une couleur
  const adjustBrightness = (color: string, percent: number): string => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <>
      <span
        ref={wrapperRef}
        className={S.statusTicketWrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowPopup(false)}
      >
        <span
          className={`${S.statusTicket} ${getStatusClass()}`}
          style={getCustomStyle()}
        >
          <span className={S.statusText}>{getStatusLabel()}</span>
        </span>
        {isPending && (
          <span aria-label="Action en attente de confirmation du capteur">
            <MdAccessTime
              className={S.statusIcon}
              style={{
                width: '25px',
                height: '25px',
                marginLeft: '6px',
              }}
            />
          </span>
        )}
      </span>

      {showPopup &&
        createPortal(
          <div
            className={S.statusPopup}
            style={{
              position: 'fixed',
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              transform: 'translate(-50%, -100%)',
              willChange: 'transform',
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <div className={S.popupHeader}>
              <div className={S.popupRow}>
                <span className={S.popupLabel}>Statut:</span>
                <span className={`${S.popupValue} ${getStatusClass()}`}>
                  {getStatusLabel()}
                </span>
              </div>
            </div>
            <div className={S.popupContent}>
              <div className={S.popupRow}>
                <span className={S.popupLabel}>Dernier signal:</span>
                <span className={S.popupValue}>
                  {lastStatusDate ? formatDate(lastStatusDate) : 'Aucune date'}
                </span>
              </div>
              {isPending && (
                <div className={S.popupRow}>
                  <span className={S.popupLabel}>Action:</span>
                  <span className={S.popupValue}>
                    Attente de confirmation du capteur
                  </span>
                </div>
              )}
            </div>
            <div className={S.popupArrow}></div>
          </div>,
          document.body
        )}
    </>
  );
};

export default StatusSensor;
